/* Copyright © ClickaByte Ltd - All Rights Reserved
  Unauthorized copying of this file, via any medium is strictly prohibited
  Proprietary and confidential
  Written by George Barnes <hello@clickabyte.co.uk>, October 2025
*/

(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PointerRipple = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const DEFAULTS = {
    color: 'rgba(255,255,255,0.9)',
    lineWidth: 1.2,
    duration: 900,
    maxRadius: 260,
    halo: false,
    haloSize: 18,
    haloOpacity: 0.35,
    haloPadding: 6,
    interactiveMode: 'auto',
    interactiveIndicator: 'outline',
    iconSize: 16,
    iconOffset: [12, 10],
    iconMap: { link: 'chevron', button: 'diamond', input: 'ibeam', control: 'plus' },
    blendMode: 'difference',
    haloMode: 'canvas',
    showCircleWithIcon: false,
    zIndex: 2147483000,
    maxDPR: 2,
    minDPR: 1,
    adaptiveDPR: true,
    dprStep: 0.125,
    fpsTarget: 59,
    dprAdjustEveryMs: 260,
    rippleOnly: false
    };

  const docEl = document.documentElement;
  const prefersReduced =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches;

  const INTERACTIVE_SELECTOR = [
    'a[href]', 'button', 'input:not([type="hidden"])', 'select', 'textarea', 'summary', 'label',
    '[role="button"]', '[role="link"]', '[contenteditable]:not([contenteditable="false"])'
  ].join(',');

  const state = {
  canvas: null, ctx: null,
  dpr: 1, w: 0, h: 0,
  raf: 0, lastT: 0,
  ripples: [],
  mouseX: 0, mouseY: 0,
  mouseActive: false,
  interactive: null,
  opts: { ...DEFAULTS },
  ro: null, bound: false,
  haloEl: null, iconEl: null, outlineEl: null,
  backdropSupported: false,
  _maskCache: new Map(),
  _handlers: null,
  _resizeScheduled: false,
  _outlineMemo: { left: -1, top: -1, width: -1, height: -1, sw: -1, rx: '' },
  _radiiCache: new WeakMap(),
  _emaDt: 16.7,
  _lastDprAdjustT: 0,
  _keepAliveFrames: 0,
  _strokeColor: '',
  _lineWidth: 1,
};

  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

  function detectBackdropSupport() {
    try {
      return !!(CSS && (CSS.supports('backdrop-filter: invert(1)') || CSS.supports('-webkit-backdrop-filter: invert(1)')));
    } catch (_) {
      return false;
    }
  }

  function createCanvas() {
    const c = document.createElement('canvas');
    c.id = 'pointer-ripple-canvas';
    const s = c.style;
    s.position = 'fixed';
    s.inset = '0';
    s.width = '100vw';
    s.height = '100vh';
    s.zIndex = String(state.opts.zIndex);
    s.pointerEvents = 'none';
    s.contain = 'strict';
    s.mixBlendMode = state.opts.blendMode || 'normal';
    docEl.appendChild(c);
    return c;
  }
  
  function _refreshStrokeCache() {
  state._strokeColor = state.opts.blendMode === 'difference' ? 'rgba(255,255,255,1)' : state.opts.color;
  state._lineWidth   = Math.max(0.5, state.opts.lineWidth);
  if (state.ctx) {
    state.ctx.lineJoin = 'round';
    state.ctx.lineCap = 'round';
  }
}

  function scheduleResize() {
  if (state._resizeScheduled) return;
  state._resizeScheduled = true;
  requestAnimationFrame(_resizeNow);
}
function _resizeNow() {
  state._resizeScheduled = false;
  if (!state.canvas) return;

  const targetDpr = clamp(
    state.opts.adaptiveDPR ? state.dpr : Math.min(window.devicePixelRatio || 1, state.opts.maxDPR),
    state.opts.minDPR, state.opts.maxDPR
  );

  const desired = state.opts.adaptiveDPR ? targetDpr : clamp(window.devicePixelRatio || 1, state.opts.minDPR, state.opts.maxDPR);
  state.dpr = desired;

  const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  state.w = vw; state.h = vh;

  const cw = Math.floor(vw * desired);
  const ch = Math.floor(vh * desired);

  if (state.canvas.width !== cw) state.canvas.width = cw;
  if (state.canvas.height !== ch) state.canvas.height = ch;

  const s = state.canvas.style;
  s.width = vw + 'px';
  s.height = vh + 'px';

  if (state.ctx) {
    state.ctx.setTransform(desired, 0, 0, desired, 0, 0);
    state.ctx.clearRect(0, 0, vw, vh);
  }
}

  function capRadiiForBox(r, w, h) {
    const maxX = w / 2; const maxY = h / 2;
    const clampCorner = (val) => Math.min(val, maxX, maxY);
    return {
      tl: clampCorner(r.tl), tr: clampCorner(r.tr),
      br: clampCorner(r.br), bl: clampCorner(r.bl),
    };
  }

  function getCornerRadii(el, rect) {
    let entry = state._radiiCache.get(el);
    if (entry && entry.rectW === rect.width && entry.rectH === rect.height) return entry.r;

    const safeParse = (v, axis) => {
      if (!v) return 0;
      v = String(v).trim();
      if (v.endsWith('%')) return (parseFloat(v) / 100) * (axis === 'x' ? rect.width : rect.height);
      const n = parseFloat(v); return Number.isFinite(n) ? Math.max(0, n) : 0;
    };

    let r;
    try {
      const cs = getComputedStyle(el);
      r = {
        tl: safeParse(cs.borderTopLeftRadius, 'x'),
        tr: safeParse(cs.borderTopRightRadius, 'x'),
        br: safeParse(cs.borderBottomRightRadius, 'x'),
        bl: safeParse(cs.borderBottomLeftRadius, 'x'),
      };
    } catch {
      r = { tl: 12, tr: 12, br: 12, bl: 12 };
    }

    if (!entry || !entry.ro) {
      const ro = ('ResizeObserver' in window) ? new ResizeObserver(() => {
        state._radiiCache.delete(el);
      }) : null;
      try { ro && ro.observe(el); } catch {}
      state._radiiCache.set(el, { r, rectW: rect.width, rectH: rect.height, ro });
    } else {
      entry.r = r; entry.rectW = rect.width; entry.rectH = rect.height;
    }
    return r;
  }

  function roundRectPathRadii(ctx, x, y, w, h, r) {
    const tl = Math.min(r.tl, w / 2, h / 2);
    const tr = Math.min(r.tr, w / 2, h / 2);
    const br = Math.min(r.br, w / 2, h / 2);
    const bl = Math.min(r.bl, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
  }

  function isFocusable(el) {
    const t = el?.getAttribute?.('tabindex');
    if (t === null || t === undefined) return false;
    const n = parseInt(t, 10);
    return !Number.isNaN(n) && n >= 0;
  }
  function isVisible(el) {
    const r = el.getBoundingClientRect();
    return r.width > 1 && r.height > 1;
  }
  function classify(el) {
    const tag = el?.tagName?.toLowerCase?.() || '';
    if (tag === 'a') return 'link';
    if (tag === 'button') return 'button';
    if (tag === 'input' || tag === 'select' || tag === 'textarea') return 'input';
    return 'control';
  }

  function findInteractiveTarget(startEl) {
    let el = startEl;
    while (el && el !== docEl) {
      if ((el.matches && el.matches(INTERACTIVE_SELECTOR)) || isFocusable(el)) {
        if (isVisible(el)) {
          const rect = el.getBoundingClientRect();
          return { el, rect, kind: classify(el) };
        }
      }
      el = el.parentElement;
    }
    return null;
  }

  function ensureBackdropNodes() {
    if (state.opts.haloMode !== 'backdrop' || !state.backdropSupported) return;

    const makeDiv = (id, z, opacityTrans = 'opacity 100ms ease-out') => {
      const d = document.createElement('div');
      d.id = id;
      const s = d.style;
      s.position = 'fixed';
      s.left = '0'; s.top = '0'; s.width = '0'; s.height = '0';
      s.pointerEvents = 'none'; s.zIndex = String(z);
      s.transform = 'translate3d(-50%, -50%, 0)';
      s.backdropFilter = 'invert(1)'; s.WebkitBackdropFilter = 'invert(1)';
      s.background = 'rgba(255,255,255,0.003)';
      s.opacity = '0'; s.transition = opacityTrans;
      s.willChange = 'transform, width, height, opacity, border-radius, -webkit-mask-image, mask-image';
      docEl.appendChild(d);
      return d;
    };

    if (!state.haloEl) state.haloEl = makeDiv('pointer-halo', state.opts.zIndex + 1, 'opacity 120ms ease-out');
    if (!state.iconEl) state.iconEl = makeDiv('pointer-halo-icon', state.opts.zIndex + 2, 'opacity 80ms ease-out');
    if (!state.outlineEl) state.outlineEl = makeDiv('pointer-outline', state.opts.zIndex + 2, 'opacity 100ms ease-out');
  }

  function getMaskURL(width, height, sw, rxTL, rxTR, rxBR, rxBL) {
    const key = `${width}|${height}|${sw}|${rxTL}|${rxTR}|${rxBR}|${rxBL}`;
    const cached = state._maskCache.get(key);
    if (cached) return cached;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>\
<rect x='${sw / 2}' y='${sw / 2}' width='${width - sw}' height='${height - sw}' rx='${rxTL}' ry='${rxTL}' fill='none' stroke='black' stroke-width='${sw}' shape-rendering='geometricPrecision'/></svg>`;
    const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
    state._maskCache.set(key, url);
    return url;
  }

  function bindEvents() {
  if (state.bound) return; 
  state.bound = true;

  const onClick = (e) => {
    const x = e.clientX, y = e.clientY;
    const spread = Math.max(state.w, state.h) * 0.22;
    state.ripples.push({ x, y, r0: 2, spread, t: performance.now() });
    state._keepAliveFrames = 6;
    start();
  };

  const onResize = () => scheduleResize();

  // --- RIPPLE ONLY: only click + resize, no pointer tracking / halo / outline
  if (state.opts.rippleOnly) {
    window.addEventListener('click', onClick, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    state._handlers = { onClick, onResize };
    return;
  }

  // --- Full mode (existing behaviour) ---
  const setInteractiveFrom = (el) => {
    const target = el ? findInteractiveTarget(el) : null;
    if (!target) { state.interactive = null; return; }
    target.radii = getCornerRadii(target.el, target.rect);
    state.interactive = target;
  };

  const samplePointer = (e) => {
    const list = (typeof e.getCoalescedEvents === 'function') ? e.getCoalescedEvents() : null;
    const last = (list && list.length) ? list[list.length - 1] : e;
    state.mouseX = last.clientX;
    state.mouseY = last.clientY;
    if (!state.mouseActive) state.mouseActive = true;
    state._keepAliveFrames = 3;
    start();
  };

  const onMove   = (e) => { samplePointer(e); };
  const onRaw    = (e) => { samplePointer(e); };
  const onLeave  = () => {
    state.mouseActive = false;
    state.interactive = null;
    hideHaloIconOutline();
  };
  const onOver   = (e) => setInteractiveFrom(e.target);
  const onOut    = (e) => {
    if (state.interactive && e.target &&
        (e.target === state.interactive.el || e.target.contains(state.interactive.el))) {
      state.interactive = null;
    }
    setInteractiveFrom(e.relatedTarget || document.elementFromPoint(state.mouseX, state.mouseY));
  };
  const onFocusIn = (e) => setInteractiveFrom(e.target);

  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerrawupdate', onRaw, { passive: true }); // ignored if unsupported
  window.addEventListener('pointerleave', onLeave, { passive: true });
  window.addEventListener('pointerover', onOver, { passive: true, capture: true });
  window.addEventListener('pointerout', onOut, { passive: true, capture: true });
  window.addEventListener('focusin', onFocusIn, { passive: true });
  window.addEventListener('click', onClick, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  state._handlers = { onMove, onRaw, onLeave, onOver, onOut, onFocusIn, onClick, onResize };
}

function unbindEvents() {
  if (!state.bound) return; 
  state.bound = false;

  const { onMove, onRaw, onLeave, onOver, onOut, onFocusIn, onClick, onResize } = state._handlers || {};

  if (onMove)    window.removeEventListener('pointermove', onMove);
  if (onRaw)     window.removeEventListener('pointerrawupdate', onRaw);
  if (onLeave)   window.removeEventListener('pointerleave', onLeave);
  if (onOver)    window.removeEventListener('pointerover', onOver, { capture: true });
  if (onOut)     window.removeEventListener('pointerout', onOut, { capture: true });
  if (onFocusIn) window.removeEventListener('focusin', onFocusIn);
  if (onClick)   window.removeEventListener('click', onClick);
  if (onResize)  window.removeEventListener('resize', onResize);

  state._handlers = null;
}


function setTransform(el, x, y) {
  el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
}

  function hideHaloIconOutline() {
    if (state.haloEl) state.haloEl.style.opacity = '0';
    if (state.iconEl) state.iconEl.style.opacity = '0';
    if (state.outlineEl) state.outlineEl.style.opacity = '0';
  }

  function step(t) {
  if (!state.ctx || !state.canvas) return;

  const rippleOnly = !!state.opts.rippleOnly;

  const ctx = state.ctx;
  const dt = state.lastT ? Math.min(33, t - state.lastT) : 16;
  state.lastT = t;

  state._emaDt = state._emaDt * 0.9 + dt * 0.1;

  let hasWork = false;

  if (state.mouseActive && state.interactive?.el) {
    const r = state.interactive.el.getBoundingClientRect();
    if (r.width !== state.interactive.rect.width || r.height !== state.interactive.rect.height ||
        r.left !== state.interactive.rect.left || r.top !== state.interactive.rect.top) {
      state.interactive.rect = r;
      state.interactive.radii = getCornerRadii(state.interactive.el, r);
    }
  }

  ctx.clearRect(0, 0, state.w, state.h);

  const useCanvasOverlay =
  !rippleOnly &&
  state.opts.halo &&
  (state.opts.haloMode !== 'backdrop' || !state.backdropSupported);

  const overInteractive = !!state.interactive;
  const overInteractiveBackdrop =
    !!(state.opts.halo && state.opts.haloMode === 'backdrop' && state.backdropSupported && state.mouseActive && overInteractive);

  if (useCanvasOverlay && state.mouseActive && !prefersReduced) {
    hasWork = true;
    ctx.globalAlpha = state.opts.haloOpacity;
    ctx.lineWidth = state._lineWidth;
    ctx.strokeStyle = state._strokeColor;

    if (overInteractive && state.opts.interactiveIndicator === 'outline') {
      const p = state.opts.haloPadding; const r = state.interactive.rect; const rad = state.interactive.radii;
      const x = (r.left - p), y = (r.top - p), w = (r.width + p * 2), h = (r.height + p * 2);
      roundRectPathRadii(ctx, x, y, w, h, { tl: rad.tl + p, tr: rad.tr + p, br: rad.br + p, bl: rad.bl + p });
      ctx.stroke();
    } else if (overInteractive && state.opts.interactiveIndicator === 'icon') {
      const h2 = (state.opts.iconSize) / 2;
      const ox = (state.opts.iconOffset?.[0] ?? 12);
      const oy = (state.opts.iconOffset?.[1] ?? 10);
      const x = state.mouseX + ox, y = state.mouseY + oy;
      ctx.beginPath(); ctx.moveTo(x, y - h2); ctx.lineTo(x + h2, y); ctx.lineTo(x, y + h2); ctx.lineTo(x - h2, y); ctx.closePath(); ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(state.mouseX, state.mouseY, state.opts.haloSize, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  if (state.haloEl) {
  if (rippleOnly) {
    // ripple-only: force hidden
    state.haloEl.style.opacity = '0';
  } else {
    const blockCircle =
      state.opts.haloMode === 'backdrop' &&
      state.backdropSupported &&
      ((overInteractiveBackdrop && state.opts.interactiveIndicator !== 'icon') ||
       (overInteractiveBackdrop && state.opts.interactiveIndicator === 'icon' && !state.opts.showCircleWithIcon));

    if (blockCircle) {
      state.haloEl.style.opacity = '0';
    } else if (state.mouseActive) {
      hasWork = true;
      const s = state.haloEl.style;
      const size = state.opts.haloSize * 2;
      if (s.width !== size + 'px') {
        s.width = size + 'px';
        s.height = size + 'px';
        s.borderRadius = '9999px';
      }
      setTransform(state.haloEl, state.mouseX, state.mouseY);
      s.opacity = '1';
    } else {
      state.haloEl.style.opacity = '0';
    }
  }
}

// ICON
if (state.iconEl) {
  if (rippleOnly) {
    state.iconEl.style.opacity = '0';
  } else if (overInteractiveBackdrop && state.opts.interactiveIndicator === 'icon') {
    hasWork = true;
    const kind = state.interactive.kind || 'control';
    const iconName = (state.opts.iconMap && state.opts.iconMap[kind]) || 'diamond';
    if (typeof window.setIconMask === 'function') {
      window.setIconMask(iconName, state.opts.iconSize, state.iconEl);
    }
    const ox = (state.opts.iconOffset?.[0] ?? 12);
    const oy = (state.opts.iconOffset?.[1] ?? 10);
    setTransform(state.iconEl, state.mouseX + ox, state.mouseY + oy);
    state.iconEl.style.opacity = '1';
  } else {
    state.iconEl.style.opacity = '0';
  }
}

// OUTLINE
if (state.outlineEl) {
  if (rippleOnly) {
    state.outlineEl.style.opacity = '0';
  } else if (overInteractiveBackdrop && state.opts.interactiveIndicator === 'outline') {
    hasWork = true;
      const s = state.haloEl.style;
      const size = state.opts.haloSize * 2;
      if (s.width !== size + 'px') { s.width = size + 'px'; s.height = size + 'px'; s.borderRadius = '9999px'; }
      setTransform(state.haloEl, state.mouseX, state.mouseY);
      s.opacity = '1';
    } else {
      state.haloEl.style.opacity = '0';
    }
  }

  if (state.iconEl) {
    if (overInteractiveBackdrop && state.opts.interactiveIndicator === 'icon') {
      hasWork = true;
      const kind = state.interactive.kind || 'control';
      const iconName = (state.opts.iconMap && state.opts.iconMap[kind]) || 'diamond';
      if (typeof window.setIconMask === 'function') window.setIconMask(iconName, state.opts.iconSize, state.iconEl);
      const ox = (state.opts.iconOffset?.[0] ?? 12);
      const oy = (state.opts.iconOffset?.[1] ?? 10);
      setTransform(state.iconEl, state.mouseX + ox, state.mouseY + oy);
      state.iconEl.style.opacity = '1';
    } else {
      state.iconEl.style.opacity = '0';
    }
  }

  if (state.outlineEl) {
    if (overInteractiveBackdrop && state.opts.interactiveIndicator === 'outline') {
      hasWork = true;
      const p = state.opts.haloPadding;
      const r = state.interactive.rect;
      const rad = state.interactive.radii;

      const left = (r.left - p);
      const top = (r.top - p);
      const width = (r.width + p * 2);
      const height = (r.height + p * 2);

      const s = state.outlineEl.style;
      if (s.width !== width + 'px') s.width = width + 'px';
      if (s.height !== height + 'px') s.height = height + 'px';
      setTransform(state.outlineEl, left + width / 2, top + height / 2);

      const sw = Math.max(1, state._lineWidth);
      const inflated = { tl: Math.max(0, rad.tl + p), tr: Math.max(0, rad.tr + p), br: Math.max(0, rad.br + p), bl: Math.max(0, rad.bl + p) };
      const capped = capRadiiForBox(inflated, width, height);
      const rxKey = `${capped.tl}|${capped.tr}|${capped.br}|${capped.bl}`;
      const memo = state._outlineMemo;
      if (memo.width !== width || memo.height !== height || memo.sw !== sw || memo.rx !== rxKey) {
        const url = getMaskURL(width, height, sw, capped.tl, capped.tr, capped.br, capped.bl);
        s.webkitMaskImage = url; s.maskImage = url;
        s.webkitMaskRepeat = 'no-repeat'; s.maskRepeat = 'no-repeat';
        s.webkitMaskSize = '100% 100%'; s.maskSize = '100% 100%';
        memo.width = width; memo.height = height; memo.sw = sw; memo.rx = rxKey;
      }
      s.opacity = '1';
    } else {
      state.outlineEl.style.opacity = '0';
    }
  }

  if (state.ripples.length) {
    hasWork = true;
    const now = performance.now();
    const life = clamp(state.opts.duration, 120, 3000);
    let w = 0;
    for (let i = 0; i < state.ripples.length; i++) {
      const r = state.ripples[i];
      if (now - r.t < life) state.ripples[w++] = r;
    }
    state.ripples.length = w;

    ctx.strokeStyle = state._strokeColor;
    ctx.lineWidth = state._lineWidth;

    for (let i = 0; i < w; i++) {
      const r = state.ripples[i];
      const k = (now - r.t) / life;
      const ease = 1 - Math.pow(1 - k, 2);
      const alpha = (1 - k) * 0.25;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(r.x, r.y, (r.r0 + ease * (r.spread || state.opts.maxRadius)), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  if (state.opts.adaptiveDPR) {
    const nowT = t;
    if (!state._lastDprAdjustT) state._lastDprAdjustT = nowT;
    if (nowT - state._lastDprAdjustT >= state.opts.dprAdjustEveryMs) {
      const targetDt = 1000 / state.opts.fpsTarget;
      const ema = state._emaDt;
      let changed = false;

      if (ema > targetDt * 1.25 && state.dpr > state.opts.minDPR) {
        state.dpr = Math.max(state.opts.minDPR, state.dpr - state.opts.dprStep);
        changed = true;
      } else if (ema < targetDt * 0.9 && state.dpr < state.opts.maxDPR) {
        state.dpr = Math.min(state.opts.maxDPR, state.dpr + state.opts.dprStep);
        changed = true;
      }

      if (changed) scheduleResize();
      state._lastDprAdjustT = nowT;
    }
  }

  if (!hasWork && state._keepAliveFrames > 0) {
    state._keepAliveFrames--;
    hasWork = true;
  }

  if (hasWork) {
    state.raf = requestAnimationFrame(step);
  } else {
    state.raf = 0; state.lastT = 0;
  }
}

  function start() {
    if (state.raf) return;
    state.lastT = 0;
    state.raf = requestAnimationFrame(step);
  }
  function stop() {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = 0;
    state.lastT = 0;
    if (state.ctx && state.canvas) state.ctx.clearRect(0, 0, state.w, state.h);
  }

  function attach(userOpts) {
  if (state.canvas) detach();
  state.opts = { ...DEFAULTS, ...(userOpts || {}) };

  if (state.opts.rippleOnly) {
    state.opts.halo = false;
    state.opts.haloMode = 'canvas';
  }

  state.backdropSupported = detectBackdropSupport();

  state.canvas = createCanvas();
  state.ctx = state.canvas.getContext('2d', { alpha: true, desynchronized: true });
  if (!state.ctx) { console.warn('[PointerRipple] 2D context unavailable.'); return; }

  _refreshStrokeCache();

  if (state.opts.halo && state.opts.haloMode === 'backdrop' && state.backdropSupported) {
    ensureBackdropNodes();
  }

  scheduleResize();

  if ('ResizeObserver' in window) {
    state.ro = new ResizeObserver(scheduleResize);
    try { state.ro.observe(document.documentElement); } catch {}
  }

  bindEvents();
}

  function detach() {
    stop(); unbindEvents();
    if (state.ro) { try { state.ro.disconnect(); } catch(_){}; state.ro = null; }

    if (state.canvas?.parentNode) state.canvas.parentNode.removeChild(state.canvas);
    if (state.haloEl?.parentNode) state.haloEl.parentNode.removeChild(state.haloEl);
    if (state.iconEl?.parentNode) state.iconEl.parentNode.removeChild(state.iconEl);
    if (state.outlineEl?.parentNode) state.outlineEl.parentNode.removeChild(state.outlineEl);

    state.canvas = null; state.ctx = null;
    state.haloEl = null; state.iconEl = null; state.outlineEl = null;

    state.ripples.length = 0;
    state.mouseActive = false;
    state.interactive = null;
    state._maskCache.clear();
    state._outlineMemo = { left: -1, top: -1, width: -1, height: -1, sw: -1, rx: '' };

    for (const [el, entry] of state._radiiCache) {
      try { entry.ro && entry.ro.disconnect(); } catch { /* ignore */ }
    }
    state._radiiCache = new WeakMap();
  }

  function _selfTest() {
    if (!state.canvas) attach();
    const centerX = state.w / 2, centerY = state.h / 2; let i = 0;
    const id = setInterval(() => {
      state.ripples.push({ x: centerX, y: centerY, r0: 2, spread: Math.max(state.w, state.h) * 0.22, t: performance.now() });
      start();
      if (++i >= 6) clearInterval(id);
    }, 90);
  }

  return { attach, detach, _selfTest };
});
